import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../hooks/axios';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import AdminPageShell from '../../components/admin/AdminPageShell';
import AdminCard from '../../components/admin/AdminCard';
import AdminFormActions from '../../components/admin/AdminFormActions';
import {
  adminAlertErrorClass,
  adminFormFullWidthClass,
  adminFormGridClass,
} from '../../components/admin/adminStyles';
import {
  WEBSITE_CONTENT_TYPES,
  WEBSITE_SITE_OPTIONS,
  emptySectionsContent,
  parseSectionsContent,
  type ContentSection,
} from './websitePageTypes';

type FormState = {
  siteKey: string;
  slug: string;
  title: string;
  subtitle: string;
  metaTitle: string;
  metaDescription: string;
  contentType: string;
  published: boolean;
  sortOrder: number;
};

const INITIAL: FormState = {
  siteKey: 'airport-transfers-taxi',
  slug: '',
  title: '',
  subtitle: '',
  metaTitle: '',
  metaDescription: '',
  contentType: 'sections',
  published: true,
  sortOrder: 0,
};

function isSectionsType(contentType: string) {
  return contentType === 'sections';
}

export default function WebsitePageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FormState>(INITIAL);
  const [sections, setSections] = useState<ContentSection[]>(
    emptySectionsContent().sections,
  );
  const [jsonContent, setJsonContent] = useState('{\n  \n}');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit || !id) return;
    axiosInstance
      .get(`/website-pages/${id}`)
      .then((res) => {
        const data = res.data;
        setForm({
          siteKey: data.siteKey ?? INITIAL.siteKey,
          slug: data.slug ?? '',
          title: data.title ?? '',
          subtitle: data.subtitle ?? '',
          metaTitle: data.metaTitle ?? '',
          metaDescription: data.metaDescription ?? '',
          contentType: data.contentType ?? 'sections',
          published: data.published ?? true,
          sortOrder: data.sortOrder ?? 0,
        });
        if (data.contentType === 'sections') {
          setSections(parseSectionsContent(data.content));
        } else {
          setJsonContent(JSON.stringify(data.content ?? {}, null, 2));
        }
      })
      .catch(() => setError('Failed to load page'));
  }, [id, isEdit]);

  const updateSection = (index: number, patch: Partial<ContentSection>) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const addSection = () => {
    setSections((prev) => [...prev, { title: '', paragraphs: [''] }]);
  };

  const removeSection = (index: number) => {
    setSections((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let content: unknown;
    if (isSectionsType(form.contentType)) {
      content = {
        sections: sections.map((s) => ({
          title: s.title.trim(),
          paragraphs: s.paragraphs
            .map((p) => p.trim())
            .filter((p) => p.length > 0),
        })),
      };
    } else {
      try {
        content = JSON.parse(jsonContent);
      } catch {
        setError('Content JSON is invalid');
        setLoading(false);
        return;
      }
    }

    const payload = {
      siteKey: form.siteKey,
      slug: form.slug,
      title: form.title,
      subtitle: form.subtitle || null,
      metaTitle: form.metaTitle || null,
      metaDescription: form.metaDescription || null,
      contentType: form.contentType,
      content,
      published: form.published,
      sortOrder: Number(form.sortOrder) || 0,
    };

    try {
      if (isEdit) await axiosInstance.put(`/website-pages/${id}`, payload);
      else await axiosInstance.post('/website-pages', payload);
      navigate('/website-pages');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    'h-11 w-full rounded-lg border border-gray-200 bg-white px-4 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';
  const labelClass = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';
  const textareaClass =
    'w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle={isEdit ? 'Edit Website Page' : 'Add Website Page'}
        description="Create or update page content stored in the database"
      />

      <AdminCard>
        {error && <div className={`mb-4 ${adminAlertErrorClass}`}>{error}</div>}
        <form onSubmit={handleSubmit} className={`${adminFormGridClass} w-full`}>
          <div>
            <label className={labelClass}>Site</label>
            <select
              className={fieldClass}
              value={form.siteKey}
              onChange={(e) => setForm((f) => ({ ...f, siteKey: e.target.value }))}
              required
            >
              {WEBSITE_SITE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Slug</label>
            <input
              className={fieldClass}
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="about / privacy / terms / faq"
              required
            />
          </div>

          <div>
            <label className={labelClass}>Title</label>
            <input
              className={fieldClass}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Subtitle</label>
            <input
              className={fieldClass}
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
            />
          </div>

          <div>
            <label className={labelClass}>Content type</label>
            <select
              className={fieldClass}
              value={form.contentType}
              onChange={(e) => {
                const next = e.target.value;
                setForm((f) => ({ ...f, contentType: next }));
                if (next === 'sections' && !isSectionsType(form.contentType)) {
                  setSections(emptySectionsContent().sections);
                }
              }}
              required
            >
              {WEBSITE_CONTENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Sort order</label>
            <input
              type="number"
              className={fieldClass}
              value={form.sortOrder}
              onChange={(e) =>
                setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))
              }
            />
          </div>

          <div>
            <label className={labelClass}>Meta title (SEO)</label>
            <input
              className={fieldClass}
              value={form.metaTitle}
              onChange={(e) => setForm((f) => ({ ...f, metaTitle: e.target.value }))}
            />
          </div>

          <div>
            <label className={labelClass}>Meta description (SEO)</label>
            <input
              className={fieldClass}
              value={form.metaDescription}
              onChange={(e) =>
                setForm((f) => ({ ...f, metaDescription: e.target.value }))
              }
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2 xl:col-span-3">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
            />
            <span>Published (visible on the website)</span>
          </label>

          {isSectionsType(form.contentType) ? (
            <div className={`${adminFormFullWidthClass} space-y-4`}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                  Page sections
                </h3>
                <button
                  type="button"
                  onClick={addSection}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200"
                >
                  Add section
                </button>
              </div>

              {sections.map((section, index) => (
                <div
                  key={index}
                  className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <label className={labelClass}>Section title</label>
                      <input
                        className={fieldClass}
                        value={section.title}
                        onChange={(e) =>
                          updateSection(index, { title: e.target.value })
                        }
                        required
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSection(index)}
                      className="mt-7 text-sm text-error-600 hover:underline"
                      disabled={sections.length <= 1}
                    >
                      Remove
                    </button>
                  </div>
                  <div>
                    <label className={labelClass}>
                      Paragraphs (one paragraph per block; blank lines start a new paragraph)
                    </label>
                    <textarea
                      className={textareaClass}
                      rows={8}
                      value={section.paragraphs.join('\n\n')}
                      onChange={(e) =>
                        updateSection(index, {
                          paragraphs: e.target.value
                            .split(/\n\s*\n/)
                            .map((p) => p.trim()),
                        })
                      }
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={adminFormFullWidthClass}>
              <label className={labelClass}>
                Content JSON (must match the site renderer for this content type)
              </label>
              <textarea
                className={`${textareaClass} font-mono text-xs`}
                rows={18}
                value={jsonContent}
                onChange={(e) => setJsonContent(e.target.value)}
                required
              />
            </div>
          )}

          <div className={adminFormFullWidthClass}>
            <AdminFormActions listRoute="/website-pages" saving={loading} />
          </div>
        </form>
      </AdminCard>
    </AdminPageShell>
  );
}
